import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { Button } from './ui/button';
import { Home, Heart, User, LogOut, Menu, X } from 'lucide-react';

import { useLocalizedNavigation } from '../hooks/useLocalizedNavigation';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLocalizedNavigation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate(getLocalizedPath('/'));
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={getLocalizedPath('/')} className="flex items-center space-x-2 group">
            <div className="bg-blue-600 p-2 rounded-lg transition-transform group-hover:scale-105">
              <Home className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              Realty Paraguay
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              to={getLocalizedPath('/properties')}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              {t('navigation.buy')}
            </Link>
            <Link
              to={getLocalizedPath('/properties?type=rent')}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              {t('navigation.rent')}
            </Link>
            <Link
              to={getLocalizedPath('/#services')}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              {t('navigation.sell')}
            </Link>
            {user && (
              <Link
                to={getLocalizedPath('/saved')}
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                <Heart className="h-4 w-4" />
                <span>{t('navigation.saved')}</span>
              </Link>
            )}
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            {user ? (
              <>
                <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg">
                  <User className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">{user.name}</span>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="sm"
                  className="text-gray-700 hover:text-red-600"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('navigation.logout')}
                </Button>
              </>
            ) : (
              <>
                {/* Login and Signup buttons hidden as requested */}
                {/* <Button
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  className="text-gray-700 hover:text-blue-600"
                >
                  {t('navigation.login')}
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {t('navigation.signup')}
                </Button> */}
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            <Link
              to={getLocalizedPath('/properties')}
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-gray-700 hover:text-blue-600 font-medium"
            >
              {t('navigation.buy')}
            </Link>
            <Link
              to={getLocalizedPath('/properties?type=rent')}
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-gray-700 hover:text-blue-600 font-medium"
            >
              {t('navigation.rent')}
            </Link>
            <Link
              to={getLocalizedPath('/#services')}
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-gray-700 hover:text-blue-600 font-medium"
            >
              {t('navigation.sell')}
            </Link>
            {user && (
              <Link
                to={getLocalizedPath('/saved')}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center space-x-2 py-2 text-gray-700 hover:text-blue-600 font-medium"
              >
                <Heart className="h-4 w-4" />
                <span>{t('navigation.saved')}</span>
              </Link>
            )}
            <div className="pt-3 border-t border-gray-200 space-y-4">
              <LanguageSwitcher />
              {user ? (
                <>
                  <div className="flex items-center space-x-2 py-2 text-gray-700">
                    <User className="h-4 w-4" />
                    <span className="font-medium">{user.name}</span>
                  </div>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('navigation.logout')}
                  </Button>
                </>
              ) : (
                <div className="space-y-2">
                  {/* Login and Signup buttons hidden as requested */}
                  {/* <Button
                    onClick={() => {
                      navigate('/login');
                      setMobileMenuOpen(false);
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    {t('navigation.login')}
                  </Button>
                  <Button
                    onClick={() => {
                      navigate('/signup');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {t('navigation.signup')}
                  </Button> */}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
